import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Briefcase, Calendar, Eye, MapPin, X,
  Scissors, Image, Sparkles, BookOpen, Clapperboard,
  Smartphone, Film, Zap, Camera, Video,
} from 'lucide-react';
import { apiGet } from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonRow } from '../../components/ui/Skeleton';
import { Button } from '../../components/ui/Button';
import { useAuthStore } from '../../store/authStore';
import type { MyTasksData, Collaborator } from '../../api/types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const STATUS_COLOR_MAP: Record<string, string> = {
  PREVENTIVO: 'var(--color-text-faint)',
  TRATTATIVA: 'var(--color-warning)',
  CONFERMATO: 'var(--color-primary)',
  IN_CORSO: 'var(--color-success)',
  COMPLETATO: 'var(--color-text-faint)',
  ARCHIVIATO: 'var(--color-text-faint)',
};

const STATUS_LABEL_MAP: Record<string, string> = {
  PREVENTIVO: 'Preventivo',
  TRATTATIVA: 'Trattativa',
  CONTRATTO: 'Contratto',
  CONFERMATO: 'Confermato',
  IN_LAVORAZIONE: 'In lavorazione',
  IN_CORSO: 'In corso',
  COMPLETATO: 'Completato',
  ARCHIVIATO: 'Archiviato',
};

const PAYMENT_STATUS_MAP: Record<string, string> = {
  DA_PAGARE: 'Da pagare',
  PARZIALE: 'Parziale',
  PAGATO: 'Pagato',
};

function getStatusColor(status: string): string {
  return STATUS_COLOR_MAP[status] || 'rgba(255,255,255,0.10)';
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return format(date, 'MMM d', { locale: it });
  } catch {
    return '';
  }
}

function formatClientName(client?: { firstName: string; lastName: string }): string {
  if (!client) return '';
  return `${client.firstName} ${client.lastName}`.trim();
}

interface EventCardProps {
  event: {
    id: string;
    title: string;
    eventDate: string;
    status: string;
    location?: string;
    eventType: string;
    client?: { firstName: string; lastName: string };
  };
  showClientName?: boolean;
  fee?: number;
  role?: string;
}

function EventCard({ event, showClientName = true, fee, role }: EventCardProps) {
  const navigate = useNavigate();
  const borderColor = getStatusColor(event.status);
  const clientName = formatClientName(event.client);

  return (
    <div
      onClick={() => navigate(`/events/${event.id}`)}
      style={{
        display: 'flex',
        gap: '1rem',
        padding: '1rem',
        borderLeft: `3px solid ${borderColor}`,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        background: 'var(--glass-bg-card)',
        cursor: 'pointer',
        transition: 'background var(--transition-interactive)',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--glass-bg-card)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--glass-bg-card)';
      }}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '60px',
        textAlign: 'center',
        paddingTop: '0.25rem',
      }}>
        <div style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 600,
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          {formatDate(event.eventDate).split(' ')[0]}
        </div>
        <div style={{
          fontSize: '1.5rem',
          fontWeight: 700,
          color: borderColor,
          lineHeight: 1,
        }}>
          {formatDate(event.eventDate).split(' ')[1]}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 600,
          color: 'var(--color-text)',
          marginBottom: '0.25rem',
          wordBreak: 'break-word',
        }}>
          {event.title}
        </div>
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          marginBottom: '0.5rem',
          flexWrap: 'wrap',
        }}>
          {showClientName && clientName && (
            <span style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
            }}>
              {clientName}
            </span>
          )}
          {event.location && (
            <span style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-muted)',
              paddingLeft: showClientName && clientName ? '0.5rem' : '0',
              borderLeft: showClientName && clientName ? '1px solid rgba(255,255,255,0.08)' : 'none',
            }}>
              <MapPin size={10} style={{ flexShrink: 0 }} /> {event.location}
            </span>
          )}
        </div>
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          <span style={{
            fontSize: 'var(--text-xs)',
            background: '#6B7280',
            color: '#FFFFFF',
            padding: '0.25rem 0.5rem',
            borderRadius: 'var(--radius-interactive)',
            fontVariant: 'all-small-caps',
          }}>
            {event.eventType?.toLowerCase() || ''}
          </span>
          <span style={{
            fontSize: 'var(--text-xs)',
            background: borderColor,
            color: '#FFFFFF',
            padding: '0.25rem 0.5rem',
            borderRadius: 'var(--radius-interactive)',
            fontWeight: 500,
          }}>
            {STATUS_LABEL_MAP[event.status] || event.status}
          </span>
          {role && (
            <span style={{
              fontSize: 'var(--text-xs)',
              background: 'var(--glass-bg-card)',
              color: 'var(--color-text)',
              padding: '0.25rem 0.5rem',
              borderRadius: 'var(--radius-interactive)',
              fontWeight: 500,
            }}>
              {role}
            </span>
          )}
          {fee && (
            <span style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-primary)',
              fontWeight: 600,
              marginLeft: 'auto',
            }}>
              €{(typeof fee === 'string' ? parseFloat(fee) : fee).toFixed(2)}
            </span>
          )}
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        color: 'var(--color-text-muted)',
      }}>
        <ArrowRight size={16} />
      </div>
    </div>
  );
}

const TASK_TYPE_ICON: Record<string, React.ReactNode> = {
  SCARTO:               <Scissors size={16} />,
  EDITING_FOTO:         <Image size={16} />,
  RITOCCO_FOTO:         <Sparkles size={16} />,
  IMPAGINAZIONE_ALBUM:  <BookOpen size={16} />,
  MONTAGGIO_PRINCIPALE: <Clapperboard size={16} />,
  REEL:                 <Smartphone size={16} />,
  TRAILER:              <Film size={16} />,
  SDE:                  <Zap size={16} />,
  ALTRO_FOTO:           <Camera size={16} />,
  ALTRO_VIDEO:          <Video size={16} />,
};

const TASK_TYPE_COLOR: Record<string, string> = {
  SCARTO:               'var(--color-text-muted)',
  EDITING_FOTO:         'var(--color-primary)',
  RITOCCO_FOTO:         'var(--color-gold)',
  IMPAGINAZIONE_ALBUM:  'var(--color-warning)',
  MONTAGGIO_PRINCIPALE: 'var(--color-gold)',
  REEL:                 'var(--color-error)',
  TRAILER:              'var(--color-error)',
  SDE:                  'var(--color-warning)',
  ALTRO_FOTO:           'var(--color-primary)',
  ALTRO_VIDEO:          'var(--color-gold)',
};

const TASK_TYPE_LABEL: Record<string, string> = {
  SCARTO:               'Scarto / Selezione',
  EDITING_FOTO:         'Editing fotografie',
  RITOCCO_FOTO:         'Ritocco avanzato',
  IMPAGINAZIONE_ALBUM:  'Impaginazione album',
  MONTAGGIO_PRINCIPALE: 'Montaggio principale',
  REEL:                 'Reel / Social',
  TRAILER:              'Trailer',
  SDE:                  'Same Day Edit',
  ALTRO_FOTO:           'Altro (Foto)',
  ALTRO_VIDEO:          'Altro (Video)',
};

interface ProjectCardProps {
  assignment: {
    id: string;
    role: string;
    paymentStatus: string;
    feeExpected?: number;
    feePaid?: number;
    project: {
      id: string;
      title: string;
      status: string;
      type: string;
    };
  };
}

function ProjectCard({ assignment }: ProjectCardProps) {
  const navigate = useNavigate();
  const { project, role, paymentStatus, feeExpected, feePaid } = assignment;
  const taskType = (project as any).postProdTaskType as string | undefined;
  const icon = taskType && TASK_TYPE_ICON[taskType] ? TASK_TYPE_ICON[taskType] : <Briefcase size={16} />;
  const iconColor = taskType && TASK_TYPE_COLOR[taskType] ? TASK_TYPE_COLOR[taskType] : 'var(--color-primary)';
  const typeLabel = taskType ? TASK_TYPE_LABEL[taskType] : (project.type?.toLowerCase() || 'Progetto');
  const roleLabel = taskType ? TASK_TYPE_LABEL[taskType] : role;

  return (
    <div
      onClick={() => navigate(`/projects/${project.id}`)}
      style={{
        display: 'flex',
        gap: '1rem',
        padding: '1rem',
        borderLeft: `3px solid ${iconColor}`,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        background: 'var(--glass-bg-card)',
        cursor: 'pointer',
        transition: 'background var(--transition-interactive)',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--glass-bg-card)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.background = 'var(--glass-bg-card)';
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '46px',
        height: '46px',
        background: `color-mix(in oklab, ${iconColor} 12%, transparent)`,
        borderRadius: 'var(--radius-card)',
        color: iconColor,
      }}>
        {icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 600,
          color: 'var(--color-text)',
          marginBottom: '0.25rem',
          wordBreak: 'break-word',
        }}>
          {project.title}
        </div>
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          <span style={{
            fontSize: 'var(--text-xs)',
            background: 'var(--glass-bg-card)',
            color: 'var(--color-text-muted)',
            padding: '0.25rem 0.5rem',
            borderRadius: 'var(--radius-interactive)',
            textTransform: 'capitalize',
          }}>
            {typeLabel}
          </span>
          {role && (
            <span style={{
              fontSize: 'var(--text-xs)',
              background: 'var(--glass-bg-card)',
              color: 'var(--color-text)',
              padding: '0.25rem 0.5rem',
              borderRadius: 'var(--radius-interactive)',
              fontWeight: 500,
            }}>
              {roleLabel}
            </span>
          )}
          {paymentStatus && (
            <span style={{
              fontSize: 'var(--text-xs)',
              background: paymentStatus === 'PAGATO' ? 'color-mix(in oklab, var(--color-success) 15%, transparent)' : 'var(--glass-bg-card)',
              color: paymentStatus === 'PAGATO' ? 'var(--color-success)' : 'var(--color-text-muted)',
              padding: '0.25rem 0.5rem',
              borderRadius: 'var(--radius-interactive)',
              fontWeight: 500,
            }}>
              {PAYMENT_STATUS_MAP[paymentStatus] || paymentStatus}
            </span>
          )}
          {feeExpected && (
            <span style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-primary)',
              fontWeight: 600,
              marginLeft: 'auto',
            }}>
              €{(typeof feeExpected === 'string' ? parseFloat(feeExpected) : feeExpected).toFixed(2)}
            </span>
          )}
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        color: 'var(--color-text-muted)',
      }}>
        <ArrowRight size={16} />
      </div>
    </div>
  );
}

function SectionHeader({ title, count }: { title: string; count: number }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.625rem 1rem',
      background: 'rgba(255,255,255,0.04)',
      borderBottom: '1px solid rgba(255,255,255,0.10)',
      fontSize: 'var(--text-xs)',
      fontWeight: 600,
      color: 'var(--color-text-muted)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    }}>
      <span>{title}</span>
      {count > 0 && (
        <span style={{
          background: 'var(--color-primary)',
          color: 'white',
          borderRadius: 'var(--radius-full)',
          padding: '0.125rem 0.5rem',
          fontSize: 'var(--text-xs)',
          fontWeight: 700,
        }}>
          {count}
        </span>
      )}
    </div>
  );
}

const FRAME_STYLE: React.CSSProperties = {
  background: 'var(--glass-bg-card)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)',
  overflow: 'hidden',
};

interface UserOption {
  id: string;
  name: string;
  email: string;
}

export function TasksPage() {
  const user = useAuthStore(s => s.user);
  const isAdmin = user?.role === 'ADMIN';

  const [targetType, setTargetType] = useState<'user' | 'collaborator' | ''>('');
  const [targetId, setTargetId] = useState('');
  const [showAssignments, setShowAssignments] = useState(false);

  const { data: users = [] } = useQuery<UserOption[]>({
    queryKey: ['users-list'],
    queryFn: () => apiGet<UserOption[]>('/users'),
    enabled: isAdmin,
  });

  const { data: collaborators = [] } = useQuery<Collaborator[]>({
    queryKey: ['collaborators-list'],
    queryFn: () => apiGet<Collaborator[]>('/collaborators?activeOnly=true'),
    enabled: isAdmin,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => apiGet<MyTasksData>('/me/tasks'),
  });

  const tasks = data;

  const { data: assignmentsData } = useQuery({
    queryKey: ['assignments-of', targetId, targetType],
    queryFn: () =>
      apiGet<MyTasksData & { targetName: string }>(
        `/me/assignments-of?targetId=${targetId}&targetType=${targetType}`,
      ),
    enabled: showAssignments && targetId !== '' && targetType !== '',
  });

  function handleViewAssignments() {
    if (!targetType || !targetId) return;
    setShowAssignments(true);
  }

  function handleCloseAssignments() {
    setShowAssignments(false);
    setTargetType('');
    setTargetId('');
  }

  function getSelectedName(): string {
    if (!targetId) return '';
    if (targetType === 'user') {
      return users.find(u => u.id === targetId)?.name || '';
    } else if (targetType === 'collaborator') {
      return collaborators.find(c => c.id === targetId)?.name || '';
    }
    return '';
  }

  return (
    <div className="page-enter" style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <PageHeader title="Assegnazioni" subtitle="I tuoi eventi, assegnazioni e progetti">
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <select
              value={targetType}
              onChange={e => {
                setTargetType(e.target.value as typeof targetType);
                setTargetId('');
                setShowAssignments(false);
              }}
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: 'var(--text-xs)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-interactive)',
                background: 'var(--glass-bg-card)',
                color: 'var(--color-text)',
              }}
              title="Tipo"
            >
              <option value="">—</option>
              <option value="user">Utente</option>
              <option value="collaborator">Collaboratore</option>
            </select>

            <select
              value={targetId}
              onChange={e => {
                setTargetId(e.target.value);
                setShowAssignments(false);
              }}
              disabled={!targetType}
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: 'var(--text-xs)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-interactive)',
                background: 'var(--glass-bg-card)',
                color: 'var(--color-text)',
                opacity: !targetType ? 0.5 : 1,
              }}
              title="Nome"
            >
              <option value="">
                {!targetType ? '—' : 'Seleziona…'}
              </option>
              {targetType === 'user'
                ? users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))
                : targetType === 'collaborator'
                ? collaborators.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))
                : null
              }
            </select>

            {showAssignments && targetId ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.25rem 0.75rem',
                background: 'var(--color-primary-highlight)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--text-xs)',
                fontWeight: 500,
                color: 'var(--color-primary)',
              }}>
                <span>{getSelectedName()}</span>
                <button
                  onClick={handleCloseAssignments}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--color-primary)',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                icon={<Eye size={12} />}
                onClick={handleViewAssignments}
                disabled={!targetType || !targetId}
              >
                Vedi
              </Button>
            )}
          </div>
        )}
      </PageHeader>

      {/* Assignments-of section */}
      {showAssignments && assignmentsData && (
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.625rem 1.5rem',
            background: 'rgba(255,255,255,0.04)',
            borderBottom: '1px solid rgba(255,255,255,0.10)',
            fontSize: 'var(--text-sm)',
            fontWeight: 600,
            color: 'var(--color-text)',
          }}>
            <span>
              Assegnazioni di <strong>{assignmentsData.targetName}</strong>
            </span>
            <button onClick={handleCloseAssignments} style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              display: 'flex',
              alignItems: 'center',
              padding: '0.25rem',
            }}>
              <X size={16} />
            </button>
          </div>

          <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* Progetti Assegnati */}
            <div style={FRAME_STYLE}>
              <SectionHeader title="Progetti Assegnati" count={assignmentsData.assignedProjects?.length ?? 0} />
              {assignmentsData.assignedProjects && assignmentsData.assignedProjects.length > 0 ? (
                assignmentsData.assignedProjects.map(assignment => (
                  <ProjectCard key={assignment.id} assignment={assignment} />
                ))
              ) : (
                <EmptyState
                  icon={<Briefcase size={32} />}
                  title="Nessun progetto assegnato"
                  description="Nessun progetto assegnato a questa persona"
                />
              )}
            </div>

            {/* Assegnazioni Evento */}
            <div style={FRAME_STYLE}>
              <SectionHeader title="Assegnazioni Evento" count={assignmentsData.assignedSlots?.length ?? 0} />
              {assignmentsData.assignedSlots && assignmentsData.assignedSlots.length > 0 ? (
                assignmentsData.assignedSlots.map(slot => (
                  <EventCard
                    key={slot.id}
                    event={slot.team.event}
                    role={slot.role}
                    fee={slot.agreedFee ?? slot.proposedFee}
                  />
                ))
              ) : (
                <EmptyState
                  icon={<Calendar size={32} />}
                  title="Nessuna assegnazione evento"
                  description="Nessun evento assegnato a questa persona"
                />
              )}
            </div>

            {/* Owned Events */}
            <div style={FRAME_STYLE}>
              <SectionHeader title={`Lavori di ${assignmentsData.targetName}`} count={assignmentsData.ownedEvents?.length ?? 0} />
              {assignmentsData.ownedEvents && assignmentsData.ownedEvents.length > 0 ? (
                assignmentsData.ownedEvents.map(event => (
                  <EventCard key={event.id} event={event} showClientName />
                ))
              ) : (
                <EmptyState
                  icon={<Calendar size={32} />}
                  title="Nessun evento"
                  description="Nessun lavoro assegnato a questa persona"
                />
              )}
            </div>

          </div>
        </div>
      )}

      {/* Own tasks (always visible) */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '1.5rem',
        maxWidth: '100%',
      }}>
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={FRAME_STYLE}>
                <div style={{
                  padding: '0.625rem 1rem',
                  background: 'rgba(255,255,255,0.04)',
                  borderBottom: '1px solid rgba(255,255,255,0.10)',
                }}>
                  <div className="skeleton" style={{ height: '0.875rem', width: '180px', borderRadius: 'var(--radius-interactive)' }} />
                </div>
                <SkeletonRow />
                <SkeletonRow />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* Progetti Assegnati */}
            <div style={FRAME_STYLE}>
              <SectionHeader title="Progetti Assegnati" count={tasks?.assignedProjects?.length ?? 0} />
              {tasks?.assignedProjects && tasks.assignedProjects.length > 0 ? (
                tasks.assignedProjects.map(assignment => (
                  <ProjectCard key={assignment.id} assignment={assignment} />
                ))
              ) : (
                <EmptyState
                  icon={<Briefcase size={32} />}
                  title="Nessun progetto assegnato"
                  description="Non sei assegnato a nessun progetto"
                />
              )}
            </div>

            {/* Assegnazioni Evento */}
            <div style={FRAME_STYLE}>
              <SectionHeader title="Assegnazioni Evento" count={tasks?.assignedSlots?.length ?? 0} />
              {tasks?.assignedSlots && tasks.assignedSlots.length > 0 ? (
                tasks.assignedSlots.map(slot => (
                  <EventCard
                    key={slot.id}
                    event={slot.team.event}
                    role={slot.role}
                    fee={slot.agreedFee ?? slot.proposedFee}
                  />
                ))
              ) : (
                <EmptyState
                  icon={<Calendar size={32} />}
                  title="Nessuna assegnazione evento"
                  description="Non sei assegnato a nessun evento"
                />
              )}
            </div>

            {/* I Miei Lavori */}
            <div style={FRAME_STYLE}>
              <SectionHeader title="I Miei Lavori" count={tasks?.ownedEvents?.length ?? 0} />
              {tasks?.ownedEvents && tasks.ownedEvents.length > 0 ? (
                tasks.ownedEvents.map(event => (
                  <EventCard key={event.id} event={event} showClientName />
                ))
              ) : (
                <EmptyState
                  icon={<Calendar size={32} />}
                  title="Nessun evento"
                  description="Nessun lavoro assegnato in questa categoria"
                />
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
